import {useState, useEffect, JSX} from 'react';

import FlagCard from './FlagCard';

import { LanguageOption, TranslatedStatus} from  '@/interfaces/ContentTranslations';
import { BuildPath } from  '@/helpers/Paths';

interface ContentTranslationsProps {
    languages: LanguageOption []
    setLanguages: (languages: LanguageOption[]) => void
    retry: () => void
}

interface ILanguageSelect {
    languages: LanguageOption []
    toggleLanguage: (code: string) => void
}

const LanguageSelect =({languages, toggleLanguage}: ILanguageSelect ): JSX.Element => {
    return (
        <ul className='language-list'>
            {languages.map((language, idx) => (
                <FlagCard key={language.languageCode} {...{...language, index: idx, toggleLanguage}}/>
            ))}
        </ul>
    )
}


const ContentTranslations = ({languages, setLanguages, retry}: ContentTranslationsProps) => {
    const [enabledLanguages, setEnabledLanguages] = useState<string>('');

    useEffect(() => {
        if (languages.length) {
            let languageList  = languages
                .map((option) => ({
                    LocalisationOrganisationId: option.localisationOrganisationId,
                    LanguageId: option.languageId,
                    LanguageCode:  option.languageCode,
                    Name: option.name,
                    TranslationStatus: option.translationStatus,
                    Enabled: option.enabled,
                    flagRegionCode: option.flagRegionCode,
                })
            );
              
                
            setEnabledLanguages(JSON.stringify(languageList));
        }
    }, [languages])  

    function toggleLanguage(code: string) {
        setLanguages(languages.map((language) => {
            if (language.languageCode === code) {
                return {...language, enabled: !language.enabled};
            }
            return language;
        }))
    }

    function LanguageCount() {
        const totalLanguages = languages.length
        const translatedLanguages = languages
            .filter(language => language.translationStatus === TranslatedStatus.Success).length
        return <span className="language-count">{`Translated Languages: ${translatedLanguages}/${totalLanguages}`}</span>
    }

    function hasFailed():boolean {
        return languages.filter(language => language.translationStatus === TranslatedStatus.Failed).length > 0
    }

    function allSelected(): boolean {
        return languages.filter(language => !language.enabled).length === 0
    }

    function selectAll(e: any) {
        e.preventDefault();
        const selectionToggleValue = !allSelected();
        setLanguages(languages
            .map((language) => ({...language, enabled: selectionToggleValue}))
        );
    }



    return (
        <>
            <h2 className='translation-header'>Dynamic Content Translations</h2>

            <p className='translation-body'>
                Dynamically translate your content into the languages you have selected in the org admin settings menu. 
                For User Publishing and Recognition lists, we will automatically translate content items for the list language options enabled.
                For all content added into lists manually, translation options will need to be applied.
            </p>

            <input id="TargetLanguages" name="TargetLanguagesString" value={enabledLanguages} readOnly aria-hidden aria-label='Target Language'></input>
           
            {languages.length
                ? <LanguageSelect languages={languages} toggleLanguage={toggleLanguage} />
                :  (
                    <div className="translate-error translation-body">
                        <img className="translate-error-image" src={`${BuildPath()}/images/translations/NoResultsImage.svg`} alt="No Languages to Select" />
                        
                        <p className="translate-error-title">Uh Oh…it seems you don't have any language's to select.</p>
                        
                        <p className="translate-error-text">If you wish to translate content, please contact your Org Admin.</p>
                    </div>
                )
            }

            {!!languages.length &&
                <>
                <div className='section-break' />

                <footer className="translation-controls-footer">
                    <LanguageCount />

                    <div className="translation-controls-button-wrapper">
                        <button className="btn-blue" style={{minWidth: '115px'}} onClick={selectAll} aria-label={`${allSelected() ? "Deselect All": "Select All"}`}>
                            {allSelected() 
                                ? 'Deselect All' 
                                : 'Select All'
                            }
                        </button>
                        
                        {hasFailed() &&
                            <button className="btn-clear" aria-label='Retry failed languages' onClick={(event) => {event.preventDefault(); retry();}}>
                                Retry Failed Languages
                            </button>
                        }
                    </div> 
                </footer>
                </>
            }
        </>
    );
}

export default ContentTranslations;